import { JourneyMap } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { DependentEventFilter, EventIdentValues } from '@fullstory/proto/fs/api/segment';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  NamedElementRecord,
  DefinedEventRecord,
} from '@fullstory/proto/fs/services/search-settings';
import { intl } from '@fullstory/intl';
export interface GraphNode extends Pick<JourneyMap.Interaction, 'next'> {
  x: number;
  y: number;
  identHash: string;
  depth: number;
  count: number;
  nextCount: number;
  otherCount?: number;
  // nodes can be identified by a combination of identHash and depth
  getId: () => string;
}

export interface GraphLink {
  value: number;
  source: GraphNode;
  target: GraphNode;
  height: number;
  // links can be identified by a combination of source and target Ids
  getId: () => string;
}

type DirectionStrings =
  (typeof JourneyMapSettings.Direction)[keyof typeof JourneyMapSettings.Direction];
export const directionEnumToString = (key: DirectionStrings) => {
  switch (key) {
    case JourneyMapSettings.Direction.DIRECTION_INVALID:
      throw 'Invalid journey map direction';
    case JourneyMapSettings.Direction.DIRECTION_FORWARD:
      return 'forward';
    case JourneyMapSettings.Direction.DIRECTION_BACKWARD:
      return 'backward';
    default:
      assertExhaustive(key);
  }
};

export type JourneyMapSessionFilter = { values: EventIdentValues | null; hash: string };

export enum RevolveLabel {
  EXIT = 'Exit',
  ENTRY = 'Entry',
}

export enum NodeType {
  Page,
  ApiEvent,
  SeenNamed,
  ClickNamed,
  DefinedEvent,
  Other,
}

export const NodeTypeToString = {
  [NodeType.Page]: intl.formatMessage({ defaultMessage: 'Viewed Page', id: 'eCU1ay' }),
  [NodeType.ApiEvent]: intl.formatMessage({ defaultMessage: 'API Defined Event', id: 'ktsxx7' }),
  [NodeType.Other]: intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl' }),
  [NodeType.SeenNamed]: intl.formatMessage({ defaultMessage: 'Saw Named Element', id: 'QzlZGM' }),
  [NodeType.ClickNamed]: intl.formatMessage({
    defaultMessage: 'Clicked Named Element',
    id: 'oNBigA',
  }),
  [NodeType.DefinedEvent]: intl.formatMessage({
    defaultMessage: 'User Defined Event',
    id: 'zrjH98',
  }),
};

export enum CannotAddInteractionStepReason {
  NO_DATA,
  OTHER,
}

export type ActionCallbacks = {
  handleHiddenEvents?: (
    visible: boolean,
    label: string,
    identity: EventIdentValues,
    nodeType: NodeType,
  ) => void;
  handleGroupEvent?: (name: string) => void;
  handleGroupPage?: (pageId: number) => void;
  handleGroupElement?: (element: string) => void;
};

export interface JourneyMapGraphicProps {
  canAppendInteraction: boolean;
  customPivotId: string;
  // any custom api events that have been expanded into their field properties
  customVars?: { [key: string]: string };
  pivotDep?: DependentEventFilter;
  deleteFilter?: (...keys: number[]) => void;
  direction: JourneyMapSettings.Direction;
  /** filters from session playlist */
  filters?: { [key: number]: JourneyMapSessionFilter };
  getGroupByPropsDisabled: {
    event: (eventName: string) => boolean;
    page: (pageDefId: number) => boolean;
    element: (namedElementId: string) => boolean;
  };
  getNamedElement: (elementId: string) => NamedElementRecord | undefined;
  getNamedElementTooltip: (namedElementId: string) => React.ReactNode;
  getDefinedEvent: (definedEventId: string) => DefinedEventRecord | undefined;
  getDefinedEventTooltip: (definedEventId: string) => React.ReactNode;
  getApiEventTooltip: (eventName: string) => React.ReactNode;
  getPageDefTooltip: (pageDefId: number) => React.ReactNode;
  getPageName: (pageDefId: number) => string;
  actionCallbacks?: ActionCallbacks;
  inSameSession: boolean;
  journeyMap: JourneyMap;
  journeysContainerRef: React.RefObject<HTMLDivElement> | null;
  mouseInteractions: { onClick: () => void };
  clickPivotId: string;
  seenPivotId: string;
  numInteractionsIndex: number;
  pagePivotId?: number;
  // any page api vars that have been expanded into their field properties
  pageVars?: { [key: number]: string };
  resetFilters?: () => void;
  resetOnBackgroundClick?: boolean;
  scrollToRight: boolean;
  scrollToSankey: boolean;
  setFilters?: (filter: { [key: number]: JourneyMapSessionFilter }) => void;
  setVisibilityState: (_: boolean, reason?: CannotAddInteractionStepReason) => void;
  setScrollToRight: (_: boolean) => void;
  setScrollToSankey: (_: boolean) => void;
  width: number;
  viewMetric?: () => void;
  elemVars?: { [key: string]: string };
  isLinkInIntelligentPath?: (sourceNodeId: string, targetNodeId: string) => boolean;
  scrollTo: (options?: ScrollToOptions) => void;
}
